import { useEffect, useRef } from 'react';

interface UseInfiniteScrollOptions {
  threshold?: number; // Distance from bottom (in pixels) to trigger load more
  isLoading?: boolean; // Loading state to prevent multiple simultaneous loads
}

export const useInfiniteScroll = (
  loadMore: () => void,
  options: UseInfiniteScrollOptions = {}
) => {
  const { threshold = 100, isLoading = false } = options;
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const handleIntersection: IntersectionObserverCallback = (entries) => {
      if (entries.length === 0) return;
      const target = entries[0]!;
      if (target.isIntersecting && !isLoading) {
        loadMore();
      }
    };

    // Create a new IntersectionObserver
    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: null, // Use viewport as root
      rootMargin: `0px 0px ${threshold}px 0px`, // Bottom margin to trigger earlier
      threshold: 0,
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMore, threshold, isLoading]);

  // Function to set the target element to observe
  const setTargetElement = (element: HTMLElement | null) => {
    if (element && observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current.observe(element);
    }
  };

  return { setTargetElement };
};
